import { IBEError } from '@common/interfaces/be-error.interface';
import { ICollection } from '@common/interfaces/collection.interface';
import { UseQueryOptions } from '@common/interfaces/query-options.interface';
import { UID } from '@common/interfaces/uid.interface';
import { addToCollection, createCollection, updateCollection } from '@common/utils/create-collection.util';
import { customNotify, IErrorItem } from '@common/utils/custom-notify.util';
import {
    ICancelSubscription,
    IPause,
    IReactivateRequest,
    ISubscriptionAddAddonRequest,
    ISubscriptionCreateRequestBody,
    ISubscriptionList,
    ISubscriptionPauseRequestbody,
    ISubscriptionRestoreDeliveriesRequestbody,
    ISubscriptionUpdAddonRequest,
    ISubscriptionUpdateRequestBody,
    ISubscriptionWithChangesResponse,
    itemApiRequest,
    TItemV1OrderIdGetError,
    TItemV1SubscriptionGetError,
    TItemV1SubscriptionIdDeleteError,
    TItemV1SubscriptionIdPatchError,
    TItemV1SubscriptionIdPauseGetError,
    TItemV1SubscriptionIdPauseIdDeleteError,
    TItemV1SubscriptionIdPausePostError,
    TItemV1SubscriptionIdReactivatePostError,
    TItemV1SubscriptionIdRestoreDeliveriesPostError,
    TItemV1SubscriptionPostError,
} from '@src/api/item-generated-api';
import { IDeliveryConfigDto } from '@src/api/model/delivery-config.dto';
import { createOrderDto, IOrderDto } from '@src/api/model/order.dto';
import { IPauseDto } from '@src/api/model/pause.dto';
import { createSubscriptionDto, ISubscriptionDto } from '@src/api/model/subscription.dto';
import { registrationApiRequest } from '@src/api/registration-generated-api';
import { filterSubscriptions } from '@src/shared/filter-subscriptions';
import { queryClient } from '@src/shared/query-client';
import { useEffect, useMemo } from 'react';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';
import { apiHooks } from '../api-hooks';

const NAME = 'subscriptions';

export const determineLastSubscription = (subscriptions?: ICollection<ISubscriptionDto>): boolean => {
    if (subscriptions === undefined) {
        return false;
    }
    const activeSubscriptions = subscriptions.list.filter((elem) => !elem.cancelDate);
    return activeSubscriptions.length <= 1;
};

const useGetSkips = (
    queryOptions?: UseQueryOptions,
): UseQueryResult<IPause[], IBEError<TItemV1SubscriptionIdPauseGetError>> => {
    return useQuery<IPause[], IBEError<TItemV1SubscriptionIdPauseGetError>>(
        `${NAME}-skips`,
        () => {
            return registrationApiRequest.registrationV1CustomerPausesGet({}).then((res) => {
                return res.data.pauses || [];
            });
        },
        queryOptions,
    );
};

const getSkipsFromCache = (): IPause[] | undefined => {
    return queryClient.getQueryData(`${NAME}-skips`);
};

const useCreateSkip = (): UseMutationResult<
    IPause,
    IBEError<TItemV1SubscriptionIdPausePostError>,
    { subscriptionId: string; body: ISubscriptionPauseRequestbody }
> => {
    return useMutation(
        (props) => {
            return itemApiRequest
                .itemV1SubscriptionIdPausePost(props.subscriptionId, props.body, {})
                .then((res) => res.data.pause || {});
        },
        {
            mutationKey: `${NAME}-skip`,
            onSuccess: async (pause, props) => {
                const skipsFromCache = getSkipsFromCache();
                const updatedSkips = skipsFromCache ? [...skipsFromCache, pause] : [pause];

                queryClient.setQueriesData(`${NAME}-skips`, updatedSkips);
                queryClient.refetchQueries(`${NAME}-${props.subscriptionId}`);
                queryClient.refetchQueries(`${NAME}-list`);
            },
        },
    );
};

const useDeleteSkip = (): UseMutationResult<
    { pauseId: UID },
    IBEError<TItemV1SubscriptionIdPauseIdDeleteError>,
    { pauseId: UID; subscriptionId: string }
> => {
    return useMutation(
        ({ pauseId, subscriptionId }) => {
            return itemApiRequest.itemV1SubscriptionIdPauseIdDelete(subscriptionId, pauseId, {}).then(() => ({ pauseId }));
        },
        {
            mutationKey: `${NAME}-skip`,
            onSuccess: async ({ pauseId }, props) => {
                const skipsFromCache = getSkipsFromCache();
                const updatedSkips = skipsFromCache ? skipsFromCache.filter((skip) => skip.id !== pauseId) : [];

                queryClient.setQueriesData(`${NAME}-skips`, updatedSkips);

                // if (subscriptionsFromCache && sub) {
                //     const updatedSub = { ...sub, pause: undefined };

                //     queryClient.setQueriesData(`${NAME}-${sub.id}`, updatedSub);
                //     queryClient.setQueryData<ICollection<ISubscriptionDto>>(
                //         `${NAME}-list`,
                //         (): ICollection<ISubscriptionDto> => {
                //             return updateCollection(subscriptionsFromCache, sub.id || '', updatedSub);
                //         },
                //     );
                // }
                // we cannot just update sub's pause prop because nextBillingAt always updated
                queryClient.refetchQueries(`${NAME}-${props.subscriptionId}`);
                queryClient.refetchQueries(`${NAME}-list`);
            },
        },
    );
};

const useRestoreDelivery = (): UseMutationResult<
    IPause,
    IBEError<TItemV1SubscriptionIdRestoreDeliveriesPostError>,
    {
        subscriptionId: string;
        body: ISubscriptionRestoreDeliveriesRequestbody;
    }
> => {
    return useMutation(
        ({ subscriptionId, body }) => {
            return itemApiRequest
                .itemV1SubscriptionIdRestoreDeliveriesPost(subscriptionId, body, {})
                .then((res) => res.data.pause || {});
        },
        {
            mutationKey: `${NAME}-skip`,
            onSuccess: async (pause, props) => {
                queryClient.setQueriesData(`${NAME}-skips`, [pause]);
                queryClient.refetchQueries(`${NAME}-${props.subscriptionId}`);
                queryClient.refetchQueries(`${NAME}-list`);
            },
        },
    );
};

const useGetSubscriptionById = (
    id: UID,
    queryOptions?: UseQueryOptions,
): UseQueryResult<ISubscriptionDto, IBEError<TItemV1OrderIdGetError>> => {
    const queries = [];
    const productQuery = apiHooks.packHouse.useGetProducts();
    queries.push(productQuery);

    const QUERY_KEY = `${NAME}-${id}`;
    const result = useQuery<{ temp?: ISubscriptionWithChangesResponse }, IBEError<TItemV1OrderIdGetError>>(
        QUERY_KEY,
        () => {
            return itemApiRequest.itemV1SubscriptionIdGet(id, {}).then((res) => {
                return { temp: res.data };
            });
        },
        {
            ...queryOptions,
        },
    );

    queries.push(result);

    const isSuccess = queries.every((q) => q.isSuccess);
    const prev = queryClient.getQueryData(QUERY_KEY) as ISubscriptionDto | undefined;
    const data: ISubscriptionDto | undefined = useMemo(() => {
        if (isSuccess) {
            if (result.data?.temp) {
                const res = createSubscriptionDto({
                    s1: result.data?.temp?.subscription,
                    schedule: result.data?.temp?.subscription_with_scheduled_changes,
                    delivery_configuration: result.data?.temp?.delivery_configuration,
                    orderInfo: result.data.temp.next_invoice,
                    last_order: result.data?.temp?.last_order,
                    active_pauses: result.data?.temp?.active_pauses,
                });
                queryClient.setQueryData(QUERY_KEY, res);
                return res;
            }
            return prev;
        }
        return undefined;
    }, [isSuccess, result.data?.temp, prev, QUERY_KEY]);

    const errors = queries.map((q) => ({ beError: q.error as IBEError, onShown: q.remove } as IErrorItem));
    useEffect(() => {
        customNotify.error(errors);
    }, [errors]);

    return { ...result, isFetching: queries.some((q) => q.isFetching || q.isLoading), data } as unknown as ReturnType<
        typeof useGetSubscriptionById
    >;
};

const useGetLastPaidOrder = (
    id: UID,
    queryOptions?: UseQueryOptions,
): UseQueryResult<IOrderDto | undefined, IBEError<TItemV1OrderIdGetError>> => {
    const {
        error: deliveryConfigsListError,
        isFetched: deliveryConfigsFetched,
        remove: deliveryConfigsRemove,
    } = apiHooks.packHouse.useGetDeliveryConfigurationList({ limit: 1000 }, {});

    useEffect(() => {
        customNotify.error([{ beError: deliveryConfigsListError, onShown: deliveryConfigsRemove }]);
    }, [deliveryConfigsRemove, deliveryConfigsListError]);

    return useQuery<IOrderDto | undefined, IBEError<TItemV1OrderIdGetError>>(
        `${NAME}-last-paid-${id}`,
        () => {
            return itemApiRequest
                .itemV1SubscriptionIdLastOrderGet(id, {})
                .then((res) => (res.data.order ? createOrderDto(res.data.order) : undefined));
        },
        {
            ...queryOptions,
            enabled: deliveryConfigsFetched && (queryOptions?.enabled !== undefined ? queryOptions.enabled : true) && !!id,
        },
    );
};

const useAddSubscription = (): UseMutationResult<
    ISubscriptionDto,
    IBEError<TItemV1SubscriptionPostError>,
    ISubscriptionCreateRequestBody
> => {
    return useMutation(
        (props) => {
            return itemApiRequest.itemV1SubscriptionPost(props, {}).then((r) =>
                createSubscriptionDto({
                    s1: r.data.subscription,
                    schedule: r.data.subscription_with_scheduled_changes,
                    delivery_configuration: r.data.delivery_configuration,
                    last_order: r.data.last_order,
                    active_pauses: r.data.active_pauses,
                    shifted_delivery_date: r.data.shifted_delivery_date,
                    orderInfo: r.data.next_invoice,
                }),
            );
        },
        {
            mutationKey: `${NAME}-add`,
            onSuccess: async (newData) => {
                queryClient.setQueryData<ICollection<ISubscriptionDto>>(
                    `${NAME}-list`,
                    (): ICollection<ISubscriptionDto> => {
                        const prev = getSubscriptionsFromCache();
                        return addToCollection(prev, newData);
                    },
                );
            },
        },
    );
};

const useUpdateSubscription = (): UseMutationResult<
    ISubscriptionWithChangesResponse,
    IBEError<TItemV1SubscriptionIdPatchError>,
    { id: string; body: ISubscriptionUpdateRequestBody }
> => {
    return useMutation(
        (props) => {
            return itemApiRequest.itemV1SubscriptionIdPatch(props.id, props.body, {}).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-update`,
            onSuccess: async (newData) => {
                const updatedSubscription = createSubscriptionDto({
                    s1: newData.subscription,
                    schedule: newData.subscription_with_scheduled_changes,
                    delivery_configuration: newData.delivery_configuration,
                    orderInfo: newData.next_invoice,
                    active_pauses: newData.active_pauses,
                    last_order: newData.last_order,
                });
                queryClient.setQueriesData(`${NAME}-${updatedSubscription?.id}`, newData);
                queryClient.setQueryData<ICollection<ISubscriptionDto>>(
                    `${NAME}-list`,
                    (): ICollection<ISubscriptionDto> => {
                        const prev = getSubscriptionsFromCache();
                        return updateCollection(prev, updatedSubscription?.id || '', updatedSubscription);
                    },
                );
            },
        },
    );
};

const useReactivateSubscription = (): UseMutationResult<
    ISubscriptionDto,
    IBEError<TItemV1SubscriptionIdReactivatePostError>,
    { id: string; body: IReactivateRequest }
> => {
    return useMutation(
        (props) => {
            return itemApiRequest.itemV1SubscriptionIdReactivatePost(props.id, props.body, {}).then((r) =>
                createSubscriptionDto({
                    s1: r.data.subscription,
                    schedule: r.data.subscription_with_scheduled_changes,
                    delivery_configuration: r.data.delivery_configuration,
                    orderInfo: r.data.next_invoice,
                    active_pauses: r.data.active_pauses,
                    last_order: r.data.last_order,
                }),
            );
        },
        {
            mutationKey: `${NAME}-reactivate`,
            onSuccess: async (newData) => {
                queryClient.setQueriesData(`${NAME}-${newData?.id}`, newData);
                queryClient.setQueryData<ICollection<ISubscriptionDto>>(
                    `${NAME}-list`,
                    (): ICollection<ISubscriptionDto> => {
                        const prev = getSubscriptionsFromCache();
                        return updateCollection(prev, newData?.id || '', newData);
                    },
                );
            },
        },
    );
};

const useAddAddon = (): UseMutationResult<
    ISubscriptionWithChangesResponse,
    IBEError<TItemV1SubscriptionIdPatchError>,
    { id: string; body: ISubscriptionAddAddonRequest }
> => {
    return useMutation(
        (props) => {
            return itemApiRequest.itemV1SubscriptionIdAddonPost(props.id, props.body, {}).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-add-addon`,
            onSuccess: async (newData) => {
                const updatedSubscription = createSubscriptionDto({
                    s1: newData.subscription,
                    schedule: newData.subscription_with_scheduled_changes,
                    delivery_configuration: newData.delivery_configuration,
                    orderInfo: newData.next_invoice,
                    active_pauses: newData.active_pauses,
                });

                queryClient.setQueriesData(`${NAME}-${updatedSubscription?.id}`, updatedSubscription);
                apiHooks.packHouse.refetchSalesAddons(updatedSubscription.cf_delivery_configuration_id || '');

                queryClient.setQueryData<ICollection<ISubscriptionDto>>(
                    `${NAME}-list`,
                    (): ICollection<ISubscriptionDto> => {
                        const prev = getSubscriptionsFromCache();
                        return updateCollection(prev, updatedSubscription?.id || '', updatedSubscription);
                    },
                );
            },
        },
    );
};

const useUpdateAddon = (): UseMutationResult<
    ISubscriptionWithChangesResponse,
    IBEError<TItemV1SubscriptionIdPatchError>,
    { id: string; item_price_id: string; body: ISubscriptionUpdAddonRequest }
> => {
    return useMutation(
        (props) => {
            return itemApiRequest
                .itemV1SubscriptionIdAddonIdPatch(props.id, props.item_price_id, props.body, {})
                .then((r) => r.data);
            // .then((r) => createSubscriptionDto(r.data.subscription, r.data.subscription_with_scheduled_changes));
        },
        {
            mutationKey: `${NAME}-update-addon`,
            onSuccess: async (newData) => {
                const updatedSubscription = createSubscriptionDto({
                    s1: newData.subscription,
                    schedule: newData.subscription_with_scheduled_changes,
                    delivery_configuration: newData.delivery_configuration,
                    orderInfo: newData.next_invoice,
                    last_order: newData.last_order,
                    active_pauses: newData.active_pauses,
                });
                queryClient.setQueriesData(`${NAME}-${updatedSubscription?.id}`, updatedSubscription);

                apiHooks.packHouse.refetchSalesAddons(updatedSubscription.cf_delivery_configuration_id || '');

                queryClient.setQueryData<ICollection<ISubscriptionDto>>(
                    `${NAME}-list`,
                    (): ICollection<ISubscriptionDto> => {
                        const prev = getSubscriptionsFromCache();
                        return updateCollection(prev, updatedSubscription?.id || '', updatedSubscription);
                    },
                );
            },
        },
    );
};

const useRemoveAddon = (): UseMutationResult<
    ISubscriptionDto,
    IBEError<TItemV1SubscriptionIdPatchError>,
    { id: string; body: string }
> => {
    return useMutation(
        (props) => {
            return itemApiRequest.itemV1SubscriptionIdAddonIdDelete(props.id, props.body, {}).then((r) =>
                createSubscriptionDto({
                    s1: r.data.subscription,
                    schedule: r.data.subscription_with_scheduled_changes,
                    delivery_configuration: r.data.delivery_configuration,
                    orderInfo: r.data.next_invoice,
                    last_order: r.data.last_order,
                    active_pauses: r.data.active_pauses,
                }),
            );
        },
        {
            mutationKey: `${NAME}-remove-addon`,
            onSuccess: async (newData) => {
                queryClient.setQueriesData(`${NAME}-${newData?.id}`, newData);
                apiHooks.packHouse.refetchSalesAddons(newData.cf_delivery_configuration_id || '');
                queryClient.setQueryData<ICollection<ISubscriptionDto>>(
                    `${NAME}-list`,
                    (): ICollection<ISubscriptionDto> => {
                        const prev = getSubscriptionsFromCache();
                        return updateCollection(prev, newData?.id || '', newData);
                    },
                );
            },
        },
    );
};

const useCancelSubscription = (): UseMutationResult<
    ICancelSubscription,
    IBEError<TItemV1SubscriptionIdDeleteError>,
    { id: string; reason?: string; reason_text?: string }
> => {
    return useMutation(
        ({ id, reason, reason_text }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return itemApiRequest.itemV1SubscriptionIdDelete(id, { reason, reason_text } as any, {}).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-cancel`,
            onSuccess: async () => {
                // invalidate subscription-list cache to refetch it
                queryClient.invalidateQueries(`${NAME}-list`);
            },
        },
    );
};

const getSubscriptionsFromCache = (): ICollection<ISubscriptionDto> | undefined => {
    return queryClient.getQueryData(`${NAME}-list`);
};

const useGetSubscriptions = (
    queryOptions: UseQueryOptions,
): UseQueryResult<ICollection<ISubscriptionDto>, IBEError<TItemV1SubscriptionGetError>> => {
    const queries = [];
    const productQuery = apiHooks.packHouse.useGetProducts(queryOptions);
    queries.push(productQuery);

    const QUERY_KEY = `${NAME}-list`;
    const result = useQuery<{ temp?: ISubscriptionList }, IBEError<TItemV1SubscriptionGetError>>(
        QUERY_KEY,
        () => {
            return itemApiRequest.itemV1SubscriptionGet({}).then((r) => {
                return { temp: r.data };
            });
        },
        {
            ...queryOptions,
        },
    );

    queries.push(result);

    const isSuccess = queries.every((q) => q.isSuccess);
    const prev = queryClient.getQueryData(QUERY_KEY) as ICollection<ISubscriptionDto> | undefined;
    const data: ICollection<ISubscriptionDto> | undefined = useMemo(() => {
        if (isSuccess) {
            if (result.data?.temp?.subscription_list) {
                const res = filterSubscriptions(
                    createCollection(
                        result?.data.temp.subscription_list?.map((c) =>
                            createSubscriptionDto({
                                s1: c.subscription,
                                schedule: c.subscription_with_scheduled_changes,
                                delivery_configuration: c.delivery_configuration,
                                orderInfo: c.next_invoice,
                                last_order: c.last_order,
                                active_pauses: c.active_pauses,
                            }),
                        ) || [],
                        0,
                        {},
                    ),
                );
                queryClient.setQueryData(QUERY_KEY, res);

                // in order to extend the cacheTime for the derived queries, we set new default options and then change them back
                const originalDefaultOptions = queryClient.getDefaultOptions();
                queryClient.setDefaultOptions({ queries: { cacheTime: 60 * 60 * 1000 } });

                // cache last order for each subscription
                res.list.map((subscription) =>
                    queryClient.setQueryData(`last-order-${subscription.id}`, subscription.lastOrder as IOrderDto),
                );
                // cache delivery configs for each subscription
                res.list.map((subscription) =>
                    queryClient.setQueryData(
                        `delivery-config-${subscription.id}`,
                        subscription.deliveryConfig as IDeliveryConfigDto,
                    ),
                );
                // cache active pauses for each subscription
                res.list.map((subscription) =>
                    queryClient.setQueryData(`active-pauses-${subscription.id}`, subscription.pauses as IPauseDto[]),
                );

                queryClient.setDefaultOptions(originalDefaultOptions);

                return res;
            }
            return filterSubscriptions(prev);
        }
        return undefined;
    }, [isSuccess, result.data?.temp?.subscription_list, prev, QUERY_KEY]);

    const errors = queries.map((q) => ({ beError: q.error as IBEError, onShown: q.remove } as IErrorItem));
    useEffect(() => {
        customNotify.error(errors);
    }, [errors]);

    return { ...result, isFetching: queries.some((q) => q.isFetching || q.isLoading), data } as unknown as ReturnType<
        typeof useGetSubscriptions
    >;
};

export const subscriptions = {
    useGetSkips,
    useCreateSkip,
    useRestoreDelivery,
    getSkipsFromCache,
    useDeleteSkip,
    useGetLastPaidOrder,
    useAddAddon,
    useUpdateAddon,
    useRemoveAddon,
    useGetSubscriptions,
    useGetSubscriptionById,
    useAddSubscription,
    useCancelSubscription,
    useUpdateSubscription,
    getSubscriptionsFromCache,
    useReactivateSubscription,
};
